<template>
  <div class="hris-page">
    <div class="hris-page-inner">
      <div class="hris-page-header">
        <span class="hris-page-eyebrow">Training</span>
        <h1 class="hris-page-title">Learning &amp; Development</h1>
        <p class="hris-page-subtitle">Manage course catalog, training calendar, and enrollment progress.</p>
      </div>

      <div class="hris-panel" style="padding: 0; margin-bottom: 20px; overflow: hidden;">
        <q-tabs
          v-model="tab"
          active-color="green"
          indicator-color="green"
          align="left"
          inline-label
        >
          <q-tab name="courses" label="Course Catalog" icon="fas fa-book" />
          <q-tab name="calendar" label="Training Calendar" icon="fas fa-calendar-alt" />
          <q-tab name="enrollment" label="Enrollment & Progress" icon="fas fa-user-graduate" />
        </q-tabs>
      </div>

      <q-tab-panels v-model="tab" animated style="background: transparent" class="q-pa-none">
        <!-- COURSES TAB -->
        <q-tab-panel name="courses" class="q-pa-none">
          <div class="hris-toolbar">
            <q-btn
              v-if="isAdmin"
              unelevated
              class="hris-q-primary"
              icon="fas fa-plus"
              label="Add Course"
              @click="openCourseDialog(null)"
            />
            <q-btn
              unelevated
              class="hris-q-outline"
              icon="fas fa-sync"
              label="Refresh"
              @click="getCourses"
            />
            <div class="hris-toolbar-spacer"></div>
            <q-input
              v-model="courseFilter"
              placeholder="Search courses..."
              dense
              outlined
              color="green"
              style="min-width: 280px"
            >
              <template v-slot:append>
                <i class="fas fa-search"></i>
              </template>
            </q-input>
          </div>

          <div v-if="!loadingCourses && filteredCourses.length === 0" class="hris-panel">
            <div class="hris-empty">No courses available.</div>
          </div>
          <div v-else class="hris-grid">
            <div
              v-for="course in filteredCourses"
              :key="course.id"
              class="hris-content-card"
            >
              <div class="hris-content-card-body">
                <div class="hris-content-card-title">{{ course.title }}</div>
                <div class="hris-content-card-meta">
                  {{ course.is_online ? 'Online' : 'In-Person' }}
                  <span v-if="course.duration_hours"> &middot; {{ course.duration_hours }} hrs</span>
                </div>
                <p class="hris-content-card-text">{{ course.description || 'No description provided.' }}</p>
                <p v-if="course.instructor" class="hris-content-card-meta">
                  <i class="fas fa-chalkboard-teacher q-mr-xs"></i>{{ course.instructor }}
                </p>
              </div>
              <div class="hris-content-card-footer">
                <q-chip
                  v-if="myEnrollmentsByCourse[course.id]"
                  :color="getHistoryStatusColor(myEnrollmentsByCourse[course.id].status)"
                  text-color="white"
                  icon="fas fa-check-circle"
                  dense
                >
                  {{ myEnrollmentsByCourse[course.id].status === 'enrolled' ? 'ENROLLED' : myEnrollmentsByCourse[course.id].status.toUpperCase().replace('_', ' ') }}
                </q-chip>
                <q-btn
                  v-else
                  flat
                  dense
                  class="hris-q-ghost"
                  icon="fas fa-user-plus"
                  label="Enroll"
                  @click="enroll(course)"
                />
                <q-btn
                  v-if="isAdmin"
                  flat
                  round
                  dense
                  class="hris-q-ghost"
                  icon="fas fa-edit"
                  @click="openCourseDialog(course)"
                >
                  <q-tooltip>Edit Course</q-tooltip>
                </q-btn>
                <q-btn
                  v-if="isAdmin"
                  flat
                  round
                  dense
                  class="hris-q-ghost"
                  icon="fas fa-trash-alt"
                  @click="confirmDeleteCourse(course)"
                >
                  <q-tooltip>Delete Course</q-tooltip>
                </q-btn>
              </div>
            </div>
          </div>
        </q-tab-panel>

        <!-- CALENDAR TAB -->
        <q-tab-panel name="calendar" class="q-pa-none">
          <div class="hris-toolbar">
            <q-btn
              v-if="isAdmin"
              unelevated
              class="hris-q-primary"
              icon="fas fa-plus"
              label="Schedule Training"
              @click="openCalendarDialog"
            />
            <q-btn
              unelevated
              class="hris-q-outline"
              icon="fas fa-sync"
              label="Refresh"
              @click="getTrainingCalendar"
            />
            <div class="hris-toolbar-spacer"></div>
            <q-input
              v-model="calendarFilter"
              placeholder="Search events..."
              dense
              outlined
              color="green"
              style="min-width: 280px"
            >
              <template v-slot:append>
                <i class="fas fa-search"></i>
              </template>
            </q-input>
          </div>

          <div class="hris-panel">
            <q-table
              flat
              :data="calendarEvents"
              :columns="calendarColumns"
              row-key="id"
              :filter="calendarFilter"
              :loading="loadingCalendar"
              separator="cell"
            >
              <template v-slot:loading>
                <div class="hris-loading absolute-full flex flex-center" style="background: rgba(255,255,255,0.7); z-index: 1;">
                  <q-spinner color="green" size="40px" />
                  <div>Loading training calendar...</div>
                </div>
              </template>
              <template v-slot:no-data>
                <div class="hris-empty full-width">No training events found.</div>
              </template>
              <template v-slot:body="props">
                <q-tr :props="props">
                  <q-td key="event_title" :props="props">{{ props.row.event_title }}</q-td>
                  <q-td key="course" :props="props">{{ props.row.course ? props.row.course.title : 'N/A' }}</q-td>
                  <q-td key="start_time" :props="props">{{ formatDateTime(props.row.start_time) }}</q-td>
                  <q-td key="end_time" :props="props">{{ formatDateTime(props.row.end_time) }}</q-td>
                  <q-td key="venue" :props="props">{{ props.row.venue }}</q-td>
                  <q-td key="max_participants" :props="props">{{ props.row.max_participants }}</q-td>
                  <q-td key="status" :props="props">
                    <q-chip
                      :color="getEventStatusColor(props.row.status)"
                      text-color="white"
                      dense
                    >
                      {{ props.row.status.toUpperCase().replace('_', ' ') }}
                    </q-chip>
                  </q-td>
                  <q-td key="actions" :props="props" class="text-center">
                    <q-select
                      v-if="isAdmin"
                      v-model="props.row.status"
                      :options="eventStatusOptions"
                      dense
                      outlined
                      color="green"
                      style="min-width: 140px"
                      @input="(val) => updateEventStatus(props.row, val)"
                    ></q-select>
                  </q-td>
                </q-tr>
              </template>
            </q-table>
          </div>
        </q-tab-panel>

        <!-- ENROLLMENT & PROGRESS TAB -->
        <q-tab-panel name="enrollment" class="q-pa-none">
          <div class="hris-toolbar">
            <q-btn
              unelevated
              class="hris-q-outline"
              icon="fas fa-sync"
              label="Refresh"
              @click="getLearningHistory"
            />
            <div class="hris-toolbar-spacer"></div>
            <q-input
              v-model="historyFilter"
              placeholder="Search enrollments..."
              dense
              outlined
              color="green"
              style="min-width: 280px"
            >
              <template v-slot:append>
                <i class="fas fa-search"></i>
              </template>
            </q-input>
          </div>

          <div class="hris-panel">
            <q-table
              flat
              :data="learningHistory"
              :columns="historyColumns"
              row-key="id"
              :filter="historyFilter"
              :loading="loadingHistory"
              separator="cell"
            >
              <template v-slot:loading>
                <div class="hris-loading absolute-full flex flex-center" style="background: rgba(255,255,255,0.7); z-index: 1;">
                  <q-spinner color="green" size="40px" />
                  <div>Loading enrollments...</div>
                </div>
              </template>
              <template v-slot:no-data>
                <div class="hris-empty full-width">No enrollments found.</div>
              </template>
              <template v-slot:body="props">
                <q-tr :props="props">
                  <q-td v-if="isAdmin" key="employee" :props="props">
                    {{ props.row.employee ? props.row.employee.firstname + ' ' + props.row.employee.lastname : 'N/A' }}
                  </q-td>
                  <q-td key="course" :props="props">{{ props.row.course ? props.row.course.title : 'N/A' }}</q-td>
                  <q-td key="progress_pct" :props="props" style="min-width: 160px">
                    <q-linear-progress
                      :value="props.row.progress_pct / 100"
                      color="green"
                      size="12px"
                      rounded
                      class="q-mb-xs"
                    />
                    {{ props.row.progress_pct }}%
                  </q-td>
                  <q-td key="status" :props="props">
                    <q-chip
                      :color="getHistoryStatusColor(props.row.status)"
                      text-color="white"
                      dense
                    >
                      {{ props.row.status.toUpperCase() }}
                    </q-chip>
                  </q-td>
                  <q-td key="score" :props="props">{{ props.row.score !== null ? props.row.score : 'N/A' }}</q-td>
                  <q-td key="completed_at" :props="props">{{ formatDateTime(props.row.completed_at) }}</q-td>
                  <q-td key="actions" :props="props" class="text-center">
                    <q-btn
                      flat
                      round
                      dense
                      class="hris-q-ghost"
                      icon="fas fa-edit"
                      @click="openProgressDialog(props.row)"
                    >
                      <q-tooltip>Update Progress</q-tooltip>
                    </q-btn>
                  </q-td>
                </q-tr>
              </template>
            </q-table>
          </div>
        </q-tab-panel>
      </q-tab-panels>

      <!-- COURSE DIALOG -->
      <q-dialog v-model="courseDialog" persistent transition-show="fade" transition-hide="fade">
        <div class="hris-modal">
          <div class="hris-modal-header">
            <div>
              <h2 class="hris-modal-title">{{ courseForm.id ? 'Edit Course' : 'Add Course' }}</h2>
            </div>
            <q-btn flat dense round v-close-popup class="hris-q-ghost" aria-label="Close">
              <i class="fas fa-times"></i>
            </q-btn>
          </div>
          <q-form @submit="saveCourse">
            <div class="hris-modal-body">
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-input
                    v-model="courseForm.title"
                    label="Course Title"
                    outlined
                    dense
                    color="green"
                    :rules="[(val) => !!val || 'Title is required']"
                  ></q-input>
                </div>
              </div>
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-input
                    v-model="courseForm.description"
                    label="Description"
                    type="textarea"
                    outlined
                    dense
                    color="green"
                  ></q-input>
                </div>
              </div>
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-input
                    v-model="courseForm.instructor"
                    label="Instructor"
                    outlined
                    dense
                    color="green"
                  ></q-input>
                </div>
              </div>
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-toggle
                    v-model="courseForm.is_online"
                    label="Online Course"
                    color="green"
                  ></q-toggle>
                </div>
              </div>
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-input
                    v-model="courseForm.course_url"
                    label="Course URL"
                    outlined
                    dense
                    color="green"
                  ></q-input>
                </div>
              </div>
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-input
                    v-model.number="courseForm.duration_hours"
                    label="Duration (hours)"
                    type="number"
                    outlined
                    dense
                    color="green"
                  ></q-input>
                </div>
              </div>
            </div>
            <div class="hris-modal-footer">
              <q-btn unelevated class="hris-q-outline" label="Cancel" v-close-popup />
              <q-btn unelevated class="hris-q-primary" label="Save" type="submit" :loading="savingCourse" />
            </div>
          </q-form>
        </div>
      </q-dialog>

      <!-- CALENDAR DIALOG -->
      <q-dialog v-model="calendarDialog" persistent transition-show="fade" transition-hide="fade">
        <div class="hris-modal">
          <div class="hris-modal-header">
            <div>
              <h2 class="hris-modal-title">Schedule Training</h2>
            </div>
            <q-btn flat dense round v-close-popup class="hris-q-ghost" aria-label="Close">
              <i class="fas fa-times"></i>
            </q-btn>
          </div>
          <q-form @submit="saveCalendarEvent">
            <div class="hris-modal-body">
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-select
                    v-model="calendarForm.course_id"
                    label="Course"
                    :options="courseOptions"
                    option-value="id"
                    :option-label="(opt) => opt ? opt.title : ''"
                    emit-value
                    map-options
                    outlined
                    dense
                    color="green"
                    :rules="[(val) => !!val || 'Course is required']"
                  ></q-select>
                </div>
              </div>
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-input
                    v-model="calendarForm.event_title"
                    label="Event Title"
                    outlined
                    dense
                    color="green"
                    :rules="[(val) => !!val || 'Event title is required']"
                  ></q-input>
                </div>
              </div>
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-input
                    v-model="calendarForm.start_time"
                    label="Start Date & Time"
                    outlined
                    dense
                    color="green"
                  >
                    <template v-slot:append>
                      <i class="far fa-calendar-alt cursor-pointer">
                        <q-popup-proxy ref="qStartTime" transition-show="scale" transition-hide="scale">
                          <div class="q-gutter-sm q-pa-sm bg-white">
                            <q-date v-model="calendarForm.start_time" mask="YYYY-MM-DD HH:mm:ss" />
                            <q-time v-model="calendarForm.start_time" mask="YYYY-MM-DD HH:mm:ss" format24h />
                            <div class="row justify-end">
                              <q-btn label="OK" color="green" flat v-close-popup />
                            </div>
                          </div>
                        </q-popup-proxy>
                      </i>
                    </template>
                  </q-input>
                </div>
              </div>
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-input
                    v-model="calendarForm.end_time"
                    label="End Date & Time"
                    outlined
                    dense
                    color="green"
                  >
                    <template v-slot:append>
                      <i class="far fa-calendar-alt cursor-pointer">
                        <q-popup-proxy ref="qEndTime" transition-show="scale" transition-hide="scale">
                          <div class="q-gutter-sm q-pa-sm bg-white">
                            <q-date v-model="calendarForm.end_time" mask="YYYY-MM-DD HH:mm:ss" />
                            <q-time v-model="calendarForm.end_time" mask="YYYY-MM-DD HH:mm:ss" format24h />
                            <div class="row justify-end">
                              <q-btn label="OK" color="green" flat v-close-popup />
                            </div>
                          </div>
                        </q-popup-proxy>
                      </i>
                    </template>
                  </q-input>
                </div>
              </div>
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-input
                    v-model="calendarForm.venue"
                    label="Venue / Zoom Link"
                    outlined
                    dense
                    color="green"
                    :rules="[(val) => !!val || 'Venue is required']"
                  ></q-input>
                </div>
              </div>
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-input
                    v-model.number="calendarForm.max_participants"
                    label="Max Participants"
                    type="number"
                    outlined
                    dense
                    color="green"
                  ></q-input>
                </div>
              </div>
            </div>
            <div class="hris-modal-footer">
              <q-btn unelevated class="hris-q-outline" label="Cancel" v-close-popup />
              <q-btn unelevated class="hris-q-primary" label="Schedule" type="submit" :loading="savingCalendarEvent" />
            </div>
          </q-form>
        </div>
      </q-dialog>

      <!-- PROGRESS DIALOG -->
      <q-dialog v-model="progressDialog" persistent transition-show="fade" transition-hide="fade">
        <div class="hris-modal">
          <div class="hris-modal-header">
            <div>
              <h2 class="hris-modal-title">Update Progress</h2>
            </div>
            <q-btn flat dense round v-close-popup class="hris-q-ghost" aria-label="Close">
              <i class="fas fa-times"></i>
            </q-btn>
          </div>
          <q-form @submit="saveProgress">
            <div class="hris-modal-body">
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-select
                    v-model="progressForm.status"
                    label="Status"
                    :options="historyStatusOptions"
                    outlined
                    dense
                    color="green"
                  ></q-select>
                </div>
              </div>
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-slider
                    v-model="progressForm.progress_pct"
                    :min="0"
                    :max="100"
                    label
                    label-always
                    color="green"
                  ></q-slider>
                </div>
              </div>
              <div v-if="isAdmin" class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-input
                    v-model.number="progressForm.score"
                    label="Score"
                    type="number"
                    outlined
                    dense
                    color="green"
                  ></q-input>
                </div>
              </div>
            </div>
            <div class="hris-modal-footer">
              <q-btn unelevated class="hris-q-outline" label="Cancel" v-close-popup />
              <q-btn unelevated class="hris-q-primary" label="Save" type="submit" :loading="savingProgress" />
            </div>
          </q-form>
        </div>
      </q-dialog>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { date } from "quasar";
import mixins from "../../mixins";

export default {
  mixins: [mixins],
  data() {
    return {
      tab: "courses",
      courses: [],
      calendarEvents: [],
      learningHistory: [],

      courseFilter: "",
      calendarFilter: "",
      historyFilter: "",

      loadingCourses: false,
      loadingCalendar: false,
      loadingHistory: false,

      courseDialog: false,
      calendarDialog: false,
      progressDialog: false,

      savingCourse: false,
      savingCalendarEvent: false,
      savingProgress: false,

      courseForm: {
        id: null,
        title: "",
        description: "",
        instructor: "",
        is_online: true,
        course_url: "",
        duration_hours: null
      },
      calendarForm: {
        course_id: null,
        event_title: "",
        start_time: "",
        end_time: "",
        venue: "",
        max_participants: 30
      },
      progressForm: {
        id: null,
        status: "",
        progress_pct: 0,
        score: null
      },

      eventStatusOptions: ["scheduled", "in_progress", "completed", "cancelled"],
      historyStatusOptions: ["enrolled", "in_progress", "completed", "dropped"],

      calendarColumns: [
        { name: "event_title", align: "left", label: "Event", sortable: true },
        { name: "course", align: "left", label: "Course", sortable: true },
        { name: "start_time", align: "center", label: "Start", sortable: true },
        { name: "end_time", align: "center", label: "End", sortable: true },
        { name: "venue", align: "left", label: "Venue" },
        { name: "max_participants", align: "center", label: "Max Participants" },
        { name: "status", align: "center", label: "Status", sortable: true },
        { name: "actions", align: "center", label: "Actions" }
      ]
    };
  },
  computed: {
    isAdmin() {
      const roleId = Number(this.userData.role_id);
      return roleId === 0 || roleId === 1;
    },
    filteredCourses() {
      if (!this.courseFilter) return this.courses;
      const needle = this.courseFilter.toLowerCase();
      return this.courses.filter((c) => (c.title || "").toLowerCase().indexOf(needle) > -1);
    },
    courseOptions() {
      return this.courses;
    },
    myEnrollmentsByCourse() {
      const map = {};
      this.learningHistory
        .filter((h) => h.employee_id === this.userData.id)
        .forEach((h) => {
          map[h.course_id] = h;
        });
      return map;
    },
    historyColumns() {
      const cols = [];
      if (this.isAdmin) {
        cols.push({ name: "employee", align: "left", label: "Employee", sortable: true });
      }
      cols.push(
        { name: "course", align: "left", label: "Course", sortable: true },
        { name: "progress_pct", align: "left", label: "Progress" },
        { name: "status", align: "center", label: "Status", sortable: true },
        { name: "score", align: "center", label: "Score" },
        { name: "completed_at", align: "center", label: "Completed At", sortable: true },
        { name: "actions", align: "center", label: "Actions" }
      );
      return cols;
    }
  },
  created() {
    this.getCourses();
    this.getTrainingCalendar();
    this.getLearningHistory();
  },
  methods: {
    formatDate(d) {
      if (!d) return "N/A";
      return date.formatDate(d, "MMMM DD, YYYY");
    },
    formatDateTime(d) {
      if (!d) return "N/A";
      return date.formatDate(d, "MMMM DD, YYYY hh:mm A");
    },
    getHeaders() {
      return {
        headers: { Authorization: "Bearer " + this.token }
      };
    },
    getCourses() {
      this.loadingCourses = true;
      axios
        .get(this.apiUrl + "/lnd/courses", this.getHeaders())
        .then((res) => {
          if (res.data.status) {
            this.courses = res.data.data;
          }
        })
        .finally(() => {
          this.loadingCourses = false;
        });
    },
    getTrainingCalendar() {
      this.loadingCalendar = true;
      axios
        .get(this.apiUrl + "/lnd/calendar", this.getHeaders())
        .then((res) => {
          if (res.data.status) {
            this.calendarEvents = res.data.data;
          }
        })
        .finally(() => {
          this.loadingCalendar = false;
        });
    },
    getLearningHistory() {
      this.loadingHistory = true;
      axios
        .get(this.apiUrl + "/lnd/learning-history", this.getHeaders())
        .then((res) => {
          if (res.data.status) {
            this.learningHistory = res.data.data;
          }
        })
        .finally(() => {
          this.loadingHistory = false;
        });
    },
    getEventStatusColor(status) {
      switch (status) {
        case "completed":
          return "positive";
        case "in_progress":
          return "warning";
        case "cancelled":
          return "negative";
        default:
          return "primary";
      }
    },
    getHistoryStatusColor(status) {
      switch (status) {
        case "completed":
          return "positive";
        case "in_progress":
          return "warning";
        case "dropped":
          return "negative";
        case "enrolled":
          return "primary";
        default:
          return "grey";
      }
    },

    // --- COURSE OPERATIONS ---
    openCourseDialog(course) {
      if (course) {
        this.courseForm = {
          id: course.id,
          title: course.title,
          description: course.description || "",
          instructor: course.instructor || "",
          is_online: !!course.is_online,
          course_url: course.course_url || "",
          duration_hours: course.duration_hours
        };
      } else {
        this.courseForm = {
          id: null,
          title: "",
          description: "",
          instructor: "",
          is_online: true,
          course_url: "",
          duration_hours: null
        };
      }
      this.courseDialog = true;
    },
    saveCourse() {
      this.savingCourse = true;
      const isEdit = !!this.courseForm.id;
      const url = isEdit ? "/lnd/courses/update" : "/lnd/courses/create";
      axios
        .post(this.apiUrl + url, this.courseForm, this.getHeaders())
        .then((res) => {
          if (res.data.status) {
            this.$q.notify({ type: "positive", message: `Course ${isEdit ? "updated" : "created"} successfully.` });
            this.courseDialog = false;
            this.getCourses();
          } else {
            this.$q.notify({ type: "negative", message: res.data.message || "Failed to save course." });
          }
        })
        .catch(() => {
          this.$q.notify({ type: "negative", message: "Error communicating with server." });
        })
        .finally(() => {
          this.savingCourse = false;
        });
    },
    confirmDeleteCourse(course) {
      this.$q
        .dialog({
          title: "Confirm Deletion",
          message: `Are you sure you want to delete the course "${course.title}"?`,
          cancel: true,
          persistent: true
        })
        .onOk(() => {
          axios
            .post(this.apiUrl + `/lnd/courses/delete/${course.id}`, {}, this.getHeaders())
            .then((res) => {
              if (res.data.status) {
                this.$q.notify({ type: "positive", message: "Course deleted." });
                this.getCourses();
              }
            });
        });
    },
    enroll(course) {
      axios
        .post(this.apiUrl + "/lnd/courses/enroll", { course_id: course.id }, this.getHeaders())
        .then((res) => {
          if (res.data.status) {
            this.$q.notify({ type: "positive", message: "Enrolled successfully." });
            this.getLearningHistory();
          } else {
            this.$q.notify({ type: "negative", message: res.data.message || "Failed to enroll." });
          }
        })
        .catch(() => {
          this.$q.notify({ type: "negative", message: "Error communicating with server." });
        });
    },

    // --- CALENDAR OPERATIONS ---
    openCalendarDialog() {
      this.calendarForm = {
        course_id: null,
        event_title: "",
        start_time: "",
        end_time: "",
        venue: "",
        max_participants: 30
      };
      this.calendarDialog = true;
    },
    saveCalendarEvent() {
      this.savingCalendarEvent = true;
      axios
        .post(this.apiUrl + "/lnd/calendar/schedule", this.calendarForm, this.getHeaders())
        .then((res) => {
          if (res.data.status) {
            this.$q.notify({ type: "positive", message: "Training scheduled successfully." });
            this.calendarDialog = false;
            this.getTrainingCalendar();
          } else {
            this.$q.notify({ type: "negative", message: res.data.message || "Failed to schedule training." });
          }
        })
        .finally(() => {
          this.savingCalendarEvent = false;
        });
    },
    updateEventStatus(row, status) {
      axios
        .post(this.apiUrl + "/lnd/calendar/update", { id: row.id, status }, this.getHeaders())
        .then((res) => {
          if (res.data.status) {
            this.$q.notify({ type: "positive", message: "Training status updated." });
            this.getTrainingCalendar();
          }
        });
    },

    // --- PROGRESS OPERATIONS ---
    openProgressDialog(row) {
      this.progressForm = {
        id: row.id,
        status: row.status,
        progress_pct: row.progress_pct,
        score: row.score
      };
      this.progressDialog = true;
    },
    saveProgress() {
      this.savingProgress = true;
      axios
        .post(this.apiUrl + "/lnd/learning-history/update", this.progressForm, this.getHeaders())
        .then((res) => {
          if (res.data.status) {
            this.$q.notify({ type: "positive", message: "Progress updated successfully." });
            this.progressDialog = false;
            this.getLearningHistory();
          }
        })
        .finally(() => {
          this.savingProgress = false;
        });
    }
  }
};
</script>
